import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { Router, RouterModule } from '@angular/router'
import { provideAppInitializer, NgModule, ErrorHandler } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'

import { NgbModule } from '@ng-bootstrap/ng-bootstrap'

import { AppRoutes } from './app.routing'
import { AppComponent } from './app.component'

import {
    AccountInit,
    AuthService,
    ComponentResolverResolver,
    CoreModule,
    EnvironmentService,
    HandbookTypesResolver,
    LabelResolver,
    LIBRARIES_EXTRA_PROVIDERS,
    PermissionsGuard,
    RuleFirewallService,
    StandardHeaderActionProvider,
    MissingTranslationInterpolationHandler,
    CustomDateFormatsProvider,
    LangUrlInjector
} from '@surgetech-eu/core'
import { DIRECTIONS } from './app.constants'
import { CatalogModule } from '@surgetech-eu/ewc-catalog'
import { environment } from '../environments/environment'
import { HeaderMenuActionsProvider } from './header-menu-actions-provider.service'
import { HandbookTypeFakeResolver } from './HandbookTypeFakeResolver'
import { getMenuItems } from './app.roles'
import { DamModule } from '@surgetech-eu/ewc-dam'
import { AppPermissionsGuard } from './eyedes/app.permissions.guard'
import { MaterialDetailsComponent } from './eyedes/handbook/material-details/material-details.component'
import { ComponentTypeComponent } from './eyedes/handbook/component-type/component-type.component'
import { CatalogueObjectTypeComponent } from './eyedes/handbook/catalogue-object-type/catalogue-object-type.component'
import { SuppliersComponent } from './eyedes/handbook/suppliers/suppliers.component'
import { RegionOfSourcingComponent } from './eyedes/handbook/region-of-sourcing/region-of-sourcing.component'
import { BaseMaterialComponent } from './eyedes/handbook/base-material/base-material.component'
import { Libraries } from './app.libraries'
import { BaseMaterialService } from './eyedes/handbook/services/base-material.service'
import { BaseMaterialCertificationTypeService } from './eyedes/handbook/services/base-material-certification-type.service'
import { CatalogueObjectTypeService } from './eyedes/handbook/services/catalogue-object-type.service'
import { ComponentTypeService } from './eyedes/handbook/services/component-type.service'
import { CountryService } from './eyedes/handbook/services/country.service'
import { MainPartService } from './eyedes/handbook/services/main-part.service'
import { MaterialDetailsService } from './eyedes/handbook/services/material-details.service'
import { MaterialFamilyService } from './eyedes/handbook/services/material-family.service'
import { OrganizationsService } from './eyedes/handbook/services/organizations.service'
import { SupplierTypeService } from './eyedes/handbook/services/supplier-type.service'
import { SuppliersService } from './eyedes/handbook/services/suppliers.service'
import { RegionOfSourcingService } from './eyedes/handbook/services/region-of-sourcing.service'
import { UnitOfMeasureService } from './eyedes/handbook/services/unit-of-measure.service'
import { ComponentTypeResolver } from './eyedes/handbook/component-type/component-type.resolver'
import { ImageSizeLibraryExtraProvider } from './eyedes/audit/services/image-size-library-extra.provider'
import { GalvmanHandbookTypesResolver } from './galvman/galvman-handbook-type.resolover'
import { HandbookService } from './eyedes/audit/services/handbook.service'
import { PlatingMaterialsResolver } from './galvman/plating-material.resolver'
import { PlatingMaterialService } from './galvman/services/plating-material.service'
import { SettingsResolver } from './galvman/settings.resolver'
import { SettingsService } from './galvman/services/settings.service'
import { KEYEGuard, KEYEPermissions } from './roles/keye.guard'
import { TranslateModule, TranslateLoader, TranslateCompiler, MissingTranslationHandler } from '@ngx-translate/core'
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler'
import { forkJoin, Observable } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import faroInitializer from './faro-initializer'
import { GlobalErrorHandler } from './global-error-handler'

const roleLibraries = []

class MultiFileLoader implements TranslateLoader {
    constructor(private http: HttpClient) {}

    getTranslation(lang: string): Observable<any> {
        return this.http.get<{ files: string[] }>(`/assets/i18n/manifest.json`).pipe(
            switchMap(manifest => {
                const requests = manifest.files.map(file => this.http.get(`/assets/i18n/${lang}/${file}`))
                return forkJoin(requests).pipe(
                    map(responses => responses.reduce((all, res) => ({ ...all, ...res }), {}))
                )
            })
        )
    }
}

const createLoader = (http: HttpClient) => new MultiFileLoader(http)

Object.defineProperty(String.prototype, 'trim', {
    writable: false,
    value: String.prototype['safeTrim']
})

@NgModule({
    declarations: [
        AppComponent,
        MaterialDetailsComponent,
        ComponentTypeComponent,
        CatalogueObjectTypeComponent,
        SuppliersComponent,
        RegionOfSourcingComponent,
        BaseMaterialComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(AppRoutes, { onSameUrlNavigation: 'reload' }),
        FormsModule,
        NgbModule,
        CoreModule.publish({
            environment: { defaultProps: environment },
            services: { session: AuthService, firewall: RuleFirewallService },
            menu: { items: getMenuItems(window['__env']?.types || environment.types), guard: PermissionsGuard },
            directions: DIRECTIONS,
            init: [AccountInit],
            header: {
                actions: StandardHeaderActionProvider,
                contextMenuActions: HeaderMenuActionsProvider
            },
            handbook: {
                pages: roleLibraries
            }
        }),
        CatalogModule.publish({}),
        DamModule.publish(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createLoader,
                deps: [HttpClient]
            },
            compiler: { provide: TranslateCompiler, useClass: TranslateMessageFormatCompiler },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MissingTranslationInterpolationHandler
            }
        })
    ],
    providers: [
        HeaderMenuActionsProvider,
        HandbookTypeFakeResolver,
        GalvmanHandbookTypesResolver,
        HandbookTypesResolver,
        ComponentTypeResolver,
        LabelResolver,
        {
            provide: PermissionsGuard,
            useClass: AppPermissionsGuard
        },
        BaseMaterialService,
        BaseMaterialCertificationTypeService,
        CatalogueObjectTypeService,
        ComponentTypeService,
        CountryService,
        MainPartService,
        MaterialDetailsService,
        MaterialFamilyService,
        OrganizationsService,
        SupplierTypeService,
        SuppliersService,
        RegionOfSourcingService,
        UnitOfMeasureService,
        HandbookService,
        ComponentResolverResolver,
        PlatingMaterialsResolver,
        PlatingMaterialService,
        SettingsResolver,
        SettingsService,
        { provide: LIBRARIES_EXTRA_PROVIDERS, useClass: ImageSizeLibraryExtraProvider, multi: true },
        KEYEGuard,
        KEYEPermissions,
        provideHttpClient(withInterceptorsFromDi()),
        CustomDateFormatsProvider,
        LangUrlInjector,
        provideAppInitializer(faroInitializer),
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        }
    ]
})
export class AppModule {
    constructor(router: Router, environmentService: EnvironmentService) {
        router.config.unshift(...DamModule.routes)
        roleLibraries.push(...Libraries[environmentService.get().type].pages)
        router.config
            .find(route => route.path === 'settings')
            ?.children?.map(child => (child.resolve.types = Libraries[environmentService?.get()?.type]?.resolver))
    }
}
